import { Button, message, Typography } from 'antd';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import styles from './index.module.css';

import { useNavigate } from 'react-router-dom';
import { AppContext, RefreshStatus } from '../../../App';
import { ReactComponent as BackButton } from '../../../Assets/yesbank/back_button_yb.svg';
import { ReactComponent as Down } from '../../../Assets/yesbank/down-arrow.svg';
import { ReactComponent as Up } from '../../../Assets/yesbank/up-arrow.svg';
import { ReactComponent as Eyes } from '../../../Assets/yesbank/eyes.svg';
import { ReactComponent as Mail } from '../../../Assets/yesbank/mail.svg';
import { ReactComponent as ErrorIcon } from '../../../Assets/yesbank/error.svg';
import ConsentRevokeImg from '../../../Assets/yesbank/consentRevoke.png';
import {
  refreshApi,
  revokeConsentAPI,
} from '../../../Services/PFMClient/DashboardService';
import { ReactComponent as Info } from '../../../Assets/yesbank/info-icon.svg';
import { FlowType, logoBaseURL } from '../../../Utils/constants';
import { EVENTCODES, pushEventToParent } from '../../../Utils/eventUtils';
import { getAltJourney } from '../../../Utils/storageUtils';
import { altJourneyType } from '../../../Utils/types';
import { formatToINR } from '../../../Utils/utils';
import FAQ from '../FAQ';
import MutualFundCard from './MutualFundCard';
import { Sheet } from 'react-modal-sheet';
import { getImagePath } from '../../../Utils/assetUtils';

type Props = {
  onBack: () => void;
};

const YBMutualFunds: React.FC<Props> = (props: Props) => {
  const {
    startAdditionalAccountJourney,
    isAccountDetailsFetched,
    data,
    statusData,
    setStatusPolling,
    setAnalyticsStatus,
    setIsAccountDetailsFetched,
    setDataRefreshStatus,
    setIsAmountVisible,
    isAmountVisible,
    datafromAnalyticsStatus,
    forceRender,
  }: any = useContext(AppContext);

  const [scrolled, setScrolled] = useState(false);
  const [isLoad, setLoad] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [revokeConsentSheet, setRevokeConsentSheet] = useState(false);
  const [errorConsentRevoke, setErrorConsentRevoke] = useState(false);
  const [successConsentRevoke, setSuccessConsentRevoke] = useState(false);
  const [selectedRefId, setSelectedRefId] = useState<string | undefined>(
    undefined
  );
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    window.onpopstate = () => {
      pushEventToParent({ eventCode: EVENTCODES.BACK_BUTTON_MF });
      props.onBack();
    };
    return () => {
      window.onpopstate = () => {};
    };
  }, []);

  useEffect(() => {
    window.onscroll = function () {
      if (window.scrollY > 18) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.scrollTo(0, 0);
  }, []);

  const dayjs = require('dayjs');

  const refIdAccountMapping = useMemo(() => {
    var localMap: any = {};

    statusData?.current?.accountDetails?.forEach((accountDetail: any) => {
      localMap = {
        ...localMap,
        [accountDetail.referenceId]: localMap[accountDetail.referenceId]
          ? localMap[accountDetail.referenceId].concat(accountDetail)
          : [accountDetail],
      };
    });

    return localMap;
  }, [statusData?.current?.accountDetails, forceRender]);

  const mfReferenceId = useMemo(() => {
    const mfAccounts = statusData.current.accountDetails.filter(
      (accountDetail: any) =>
        accountDetail.fiType === 'MUTUAL_FUNDS' &&
        accountDetail.status !== 'INACTIVE'
    );
    return mfAccounts.length > 0 ? mfAccounts[0].referenceId : undefined;
  }, [statusData.current.accountDetails, forceRender]);

  const mfFipDetails = useMemo(() => {
    const mfAccounts = statusData.current.accountDetails.filter(
      (accountDetail: any) =>
        accountDetail.fiType === 'MUTUAL_FUNDS' &&
        accountDetail.status !== 'INACTIVE'
    );
    if (mfAccounts.length > 0) {
      return {
        fipId: mfAccounts[0].fipId,
        fipName: mfAccounts[0].fipName,
      };
    }
    return { fipId: '', fipName: '' };
  }, [statusData.current.accountDetails, forceRender]);

  const revokeConsent = () => {
    setIsButtonLoading(true);
    revokeConsentAPI({ referenceId: selectedRefId! })
      .then((_res) => {
        setAnalyticsStatus('PENDING');
        setIsAccountDetailsFetched(false);
        setStatusPolling((prev: any) => prev + 1);
        setSuccessConsentRevoke(true);
      })
      .catch((_e) => {
        setErrorConsentRevoke(true);
      })
      .finally(() => {
        setRevokeConsentSheet(false);
        setIsButtonLoading(false);
      });
  };

  const handleCloseSuccessSheet = () => {
    setSuccessConsentRevoke(false);
    navigate(-1);
  };

  const handleImageError = (event: any) => {
    event.target.src = getImagePath('images/bank.svg');
  };

  const linkedMutualFunds = useMemo(() => {
    return statusData.current.accountDetails.filter(
      (accountDetail: any) =>
        accountDetail.fiType === 'MUTUAL_FUNDS' &&
        accountDetail.status !== 'INACTIVE'
    ).length;
  }, [statusData.current.accountDetails, forceRender]);

  useEffect(() => {
    if (linkedMutualFunds === 0) {
      props.onBack();
    }
  }, [linkedMutualFunds]);

  // Process mutual funds data from analytics - no grouping, just map each scheme
  const mutualFundsInfo = useMemo(() => {
    const mutualFundsData = data?.current?.MUTUAL_FUNDS?.data;

    if (
      !mutualFundsData ||
      !mutualFundsData.mutualFundWiseAnalysis ||
      mutualFundsData.mutualFundWiseAnalysis.length === 0
    ) {
      return [];
    }

    // Return the array directly, just add folioNumbers for easy access
    return mutualFundsData.mutualFundWiseAnalysis.map((scheme: any) => ({
      ...scheme,
      folioNumbers: scheme.accountDetails?.map((acc: any) => acc.folioNo) || [],
      folioCount: scheme.accountDetails?.length || 0,
      currentValue: (scheme.units || 0) * (scheme.nav || 0),
    }));
  }, [data?.current?.MUTUAL_FUNDS, forceRender]);

  const totalMFBalance = useMemo(() => {
    // Use analytics data first, then fall back to status data
    return (
      data?.current?.MUTUAL_FUNDS?.data.overallAnalysis?.totalPortfolioValue ||
      datafromAnalyticsStatus?.current?.mutualFunds ||
      0
    );
  }, [
    data?.current?.MUTUAL_FUNDS,
    datafromAnalyticsStatus?.current?.mutualFunds,
    forceRender,
  ]);

  const totalFolios = useMemo(() => {
    return mutualFundsInfo.reduce((count: number, scheme: any) => {
      return count + (scheme.folioCount || 0);
    }, 0);
  }, [mutualFundsInfo]);

  const refreshData = () => {
    if (
      statusData.current.accountDetails.filter((accountDetail: any) => {
        return accountDetail.status === 'REVOKE_INITIATED';
      }).length !== 0
    ) {
      message.info(
        <Typography.Text style={{ fontSize: 10, fontWeight: 600 }}>
          Consent revocation in progress. Please try again in some time.
        </Typography.Text>
      );
    } else {
      pushEventToParent({
        eventCode: EVENTCODES.USER_INITIATED_REFRESH_BUTTON_CLICKED,
      });
      setLoad(true);
      refreshApi()
        .then(() => {
          setAnalyticsStatus('PENDING');
          setIsAccountDetailsFetched(false);
          setStatusPolling((prev: any) => prev + 1);
          setLoad(false);
          setDataRefreshStatus(RefreshStatus.IN_PROGRESS);
          pushEventToParent({
            eventCode: EVENTCODES.REFRESH_PROCESSING,
          });
        })
        .catch(() => {
          pushEventToParent({
            eventCode: EVENTCODES.REFRESH_FAILED,
          });
          setLoad(false);
          message.error('Something went wrong. Try again after sometime');
        });
    }
  };

  return (
    <div className={`${styles.mf_home_container}`}>
      <>
        <div
          className={styles.backButton}
          style={{ boxShadow: scrolled ? '0 4px 10px #00000029' : 'none' }}
        >
          <div
            style={{
              height: 24,
              display: 'flex',
              alignItems: 'center',
              gap: 20,
            }}
          >
            <BackButton
              onClick={() => {
                navigate(-1);
              }}
            />
            <Typography.Text
              style={{ color: '#333333', fontWeight: 700, fontSize: 13 }}
            >
              OneView of Mutual Funds
            </Typography.Text>
          </div>
        </div>

        <Typography.Text className={styles.contentStyle}>
          Refreshed on{' '}
          {dayjs(statusData.current.lastSuccessfulRefreshTime).format(
            "DD MMM 'YY"
          )}
        </Typography.Text>

        <div className={styles.mfOuterContainer}>
          <div className={styles.mfInnerContainer}>
            <div
              className={`${styles.mfHeaderContainer} ${
                getAltJourney() === altJourneyType.YESONLINE
                  ? styles.mfHeaderContainer_yesonline
                  : ''
              }`}
            >
              <div className={styles.mfHeaderTextContainer}>
                <div
                  style={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                >
                  <div
                    style={{ display: 'flex', flexDirection: 'row', gap: 8 }}
                  >
                    <Typography.Text
                      className={styles.commonHeaderSubtitleOpacity}
                    >
                      Total Mutual Fund Balance
                    </Typography.Text>
                    <Info />
                  </div>

                  <Typography.Text className={styles.commonHeaderSubtitle}>
                    {totalFolios} Folio(s)
                  </Typography.Text>
                </div>
              </div>

              <div className={styles.mfBalanceContainer}>
                <div
                  style={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                >
                  <Typography.Text className={styles.balanceLabel}>
                    Current Valuation
                  </Typography.Text>
                  <Typography.Text className={styles.commonHeaderTitle}>
                    {formatToINR(totalMFBalance.toString(), isAmountVisible)}
                    <Eyes
                      onClick={() =>
                        setIsAmountVisible((prev: boolean) => !prev)
                      }
                      style={{ cursor: 'pointer' }}
                    />
                  </Typography.Text>
                </div>
              </div>

              <Typography.Text
                className={styles.addFolioText}
                onClick={() => {
                  navigate('/add-mutual-fund');
                  pushEventToParent({
                    eventCode: EVENTCODES.ADD_ACCOUNT_MF,
                  });
                  startAdditionalAccountJourney(FlowType.MUTUAL_FUNDS);
                }}
              >
                + Add Folio
              </Typography.Text>
            </div>

            <div className={styles.mfCardsSection}>
              {(showAll ? mutualFundsInfo : mutualFundsInfo.slice(0, 3)).map(
                (scheme: any, index: number) => (
                  <MutualFundCard key={index} schemeData={scheme} />
                )
              )}

              {mutualFundsInfo.length > 3 && (
                <div className={styles.viewMoreButton}>
                  <Typography.Link
                    onClick={() => setShowAll(!showAll)}
                    className={styles.viewMoreText}
                  >
                    {showAll ? 'View Less' : 'View More'}
                    {showAll ? (
                      <Up style={{ width: 8, height: 12, marginLeft: 4 }} />
                    ) : (
                      <Down style={{ width: 8, height: 12, marginLeft: 4 }} />
                    )}
                  </Typography.Link>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className={styles.mf_help_section}>
          <div className={styles.mf_help_header}>
            <Typography.Text className={styles.mf_help_title}>
              Need more help?
            </Typography.Text>
            <Typography.Text className={styles.mf_help_subtitle}>
              Our specialists are always happy to guide you.
            </Typography.Text>
          </div>
          <div className={styles.mf_help_actions}>
            <div
              onClick={() => {
                pushEventToParent({
                  eventCode: EVENTCODES.EMAIL_MDS,
                  meta: { email: 'yestouch@yesbank.in' },
                });
                window.location.href = 'mailto:yestouch@yesbank.in';
              }}
              className={styles.mf_help_action}
            >
              <Mail className={styles.mf_help_icon} />
              <Typography.Text className={styles.mf_help_action_text}>
                E-mail Us
              </Typography.Text>
            </div>
          </div>
          {mfReferenceId && (
            <div className={styles.revokeConsentContainer}>
              <Button
                className={styles.stopTrackingButton}
                onClick={() => {
                  pushEventToParent({
                    eventCode: EVENTCODES.REVOKE_YOUR_CONSENT_CLICKED,
                  });
                  setRevokeConsentSheet(true);
                  setSelectedRefId(mfReferenceId);
                }}
              >
                <Typography.Text className={styles.revokeConsentButtonText}>
                  Stop Tracking
                </Typography.Text>
              </Button>
            </div>
          )}
        </div>

        <Sheet
          isOpen={revokeConsentSheet}
          detent='content-height'
          className='revokeConsentSheet'
          onClose={() => {
            setRevokeConsentSheet(false);
          }}
        >
          <Sheet.Backdrop
            onTap={() => setRevokeConsentSheet(false)}
          ></Sheet.Backdrop>

          <Sheet.Container>
            <div className={styles.consentRevokeSheetContainer}>
              <div className={styles.closeIconContainer}>
                <div
                  className={styles.closeIcon}
                  onClick={() => {
                    setRevokeConsentSheet(false);
                  }}
                >
                  <Typography.Text style={{ color: '#fff', fontWeight: 500 }}>
                    X
                  </Typography.Text>
                </div>
              </div>

              <Typography.Text className={styles.consentRevokeSheetTitle}>
                Do you really want to revoke the consent?
              </Typography.Text>
              <Typography.Text
                style={{ opacity: 0.7, fontSize: 12, marginTop: 4 }}
              >
                On revoking, you will not be able to get insights for the below
                folio(s).
              </Typography.Text>
              <div
                style={{
                  margin: '24px 0px 16px 0px',
                  overflow: 'auto',
                  height: '50%',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    gap: 4,
                  }}
                >
                  <div className={styles.iconAsset}>
                    <img
                      src={`${logoBaseURL}${
                        mfFipDetails.fipId.split('$')[0]
                      }.jpeg`}
                      style={{
                        objectFit: 'contain',
                        height: 14.4,
                        width: 'auto',
                      }}
                      onError={handleImageError}
                    />
                  </div>
                  <Typography.Text className={styles.fipTitle}>
                    {mfFipDetails.fipName}
                  </Typography.Text>
                </div>

                <Typography.Text
                  style={{ fontSize: 16, fontWeight: 600, marginTop: 4 }}
                >
                  Folios linked with this consent:
                </Typography.Text>
                <div className={styles.accountList}>
                  {refIdAccountMapping[selectedRefId!]?.map(
                    (mfDetails: any, index: number) => {
                      return (
                        <div
                          key={index}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            padding: '16px 0px',
                            borderBottom: '1px solid #1818191A',
                          }}
                        >
                          <Typography.Text className={styles.fipTitle}>
                            {mfDetails.fipName}
                          </Typography.Text>
                          <Typography.Text className={styles.accountNumberText}>
                            Folio: {mfDetails.maskedAccountNumber}
                          </Typography.Text>
                        </div>
                      );
                    }
                  )}
                </div>
              </div>
              <div className={styles.consentRevokeButtonContainer}>
                <Button
                  className={styles.consentRevokeGoBack}
                  onClick={() => {
                    pushEventToParent({
                      eventCode: EVENTCODES.GO_BACK_CONSENT_REVOKE,
                    });
                    setRevokeConsentSheet(false);
                  }}
                >
                  <Typography.Text
                    style={{
                      fontWeight: 600,
                      color: '#ED1F47',
                    }}
                  >
                    Go Back
                  </Typography.Text>
                </Button>
                <Button
                  loading={isButtonLoading}
                  className={styles.consentRevokeConfirm}
                  onClick={() => {
                    pushEventToParent({
                      eventCode: EVENTCODES.CONSENT_REVOKE_CLICKED,
                    });
                    revokeConsent();
                  }}
                >
                  <Typography.Text style={{ color: '#fff', fontWeight: 600 }}>
                    Revoke
                  </Typography.Text>
                </Button>
              </div>
            </div>
          </Sheet.Container>
        </Sheet>

        <Sheet
          isOpen={errorConsentRevoke}
          detent='content-height'
          className='revokeConsentSheet'
          onClose={() => {
            setErrorConsentRevoke(false);
          }}
        >
          <Sheet.Backdrop
            onTap={() => setErrorConsentRevoke(false)}
          ></Sheet.Backdrop>

          <Sheet.Container>
            <div className={styles.errorConsentRevokeSheetContainer}>
              <ErrorIcon />

              <Typography.Text
                style={{ fontSize: 20, fontWeight: 700, marginTop: 12 }}
              >
                Error
              </Typography.Text>
              <Typography.Text
                style={{
                  fontSize: 17,
                  fontWeight: 700,
                  textAlign: 'center',
                  marginTop: 4,
                }}
              >
                Unable to revoke the consent, please try again!
              </Typography.Text>
            </div>
          </Sheet.Container>
        </Sheet>

        <Sheet
          isOpen={successConsentRevoke}
          onClose={handleCloseSuccessSheet}
          detent='content-height'
          disableScrollLocking={true}
        >
          <Sheet.Container className={styles.revokedSheetContainer}>
            <Sheet.Header />
            <Sheet.Content>
              <>
                <img
                  src={ConsentRevokeImg}
                  alt='Consent Revoked'
                  className={`${styles.revokedImage} ${styles.revokedImage_yb}`}
                />
                <h4 className={styles.revokeText}>Consent Revoked!</h4>
                <button
                  className={styles.revokedButton}
                  onClick={handleCloseSuccessSheet}
                >
                  Okay
                </button>
              </>
            </Sheet.Content>
          </Sheet.Container>
          <Sheet.Backdrop />
        </Sheet>
      </>
    </div>
  );
};

export default YBMutualFunds;
